import React, { FunctionComponent } from "react";
import { Link } from "gatsby";
import Image from "gatsby-image";
import { TOPICS, TopicKey } from "../../topics";
import { ProjectSummary } from "../../types";
import { pluralize } from "../../data/format";

export interface ProgressBar {
  topic: TopicKey;
  total: number;
  filled: number;
}

export const ProgressBar: FunctionComponent<ProgressBar> = ({
  topic,
  total,
  filled,
}) => (
  <div className="flex gap-1" aria-hidden="true">
    {Array.from({ length: total }).map((_, index) => (
      <div
        key={index}
        className={`h-1.5 flex-1 ${
          index < filled ? TOPICS[topic].fillClass : "bg-line"
        }`}
      />
    ))}
  </div>
);

const LABEL = "font-display text-label font-semibold uppercase text-muted";

export const ProjectCard: FunctionComponent<ProjectSummary> = ({
  href,
  title,
  topic,
  summary,
  img,
  imgAlt,
  parts,
}) => (
  <Link
    to={href}
    className="group flex flex-col border-2 border-ink bg-paper shadow-hard transition duration-maker ease-maker hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-hard-lg"
  >
    <div className="h-[170px] border-b-2 border-ink">
      <Image fluid={img} alt={imgAlt || title} style={{ height: "100%" }} />
    </div>
    <div className="flex flex-col gap-2.5 p-[18px]">
      <div className={LABEL}>
        Project · {pluralize(parts.length, "part", "parts")}
      </div>
      <h3 className="font-display text-2xl font-extrabold tracking-[-0.02em] transition-colors duration-maker ease-maker group-hover:text-spark">
        {title}
      </h3>
      <ProgressBar topic={topic} total={parts.length} filled={parts.length} />
      <p className="text-[15px] leading-[1.5] text-body-2">{summary}</p>
    </div>
  </Link>
);

export interface ProjectCardCompact {
  project: ProjectSummary;
  currentHref: string;
}

/** Sidebar card on a post page: progress up to this post plus the numbered parts. */
export const ProjectCardCompact: FunctionComponent<ProjectCardCompact> = ({
  project,
  currentHref,
}) => {
  const current = project.parts.find((part) => part.href === currentHref);
  return (
    <div className="flex flex-col gap-2.5 border-2 border-ink bg-paper p-[18px] shadow-hard">
      <div className={LABEL}>Project</div>
      <Link
        to={project.href}
        className="font-display text-xl font-extrabold transition-colors duration-maker ease-maker hover:text-spark"
      >
        {project.title}
      </Link>
      <ProgressBar
        topic={project.topic}
        total={project.parts.length}
        filled={current ? project.parts.indexOf(current) + 1 : 0}
      />
      <ol className="flex flex-col gap-1.5 font-display text-ui">
        {project.parts.map((part) =>
          part === current ? (
            <li key={part.href} className="font-semibold">
              {part.part}. This post
            </li>
          ) : (
            <li key={part.href}>
              <Link to={part.href} className="text-muted hover:text-spark">
                {part.part}. {part.title}
              </Link>
            </li>
          )
        )}
      </ol>
    </div>
  );
};
